import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, InputAdornment, makeStyles } from "@material-ui/core";

import Panel from "../blocks/Panel";
import MapIcon from "@material-ui/icons/Map";
import TimeIcon from "@material-ui/icons/Timelapse";
import CoffeeIcon from "@material-ui/icons/FreeBreakfast";
import VehicleAutocomplete from "../../project/VehicleAutocomplete";
import clsx from "clsx";
import {
  handleKeypress,
  secondsConvert,
  getHoursMins
} from "../../../utils/helpers";
import Form from "../../common/Form";
import LightTooltip from "../../common/LightTooltip";
import { useSelector } from "react-redux";

const useStyles = makeStyles(theme => ({
  gridContentLocations: {
    paddingTop: theme.spacing(1.5) + "px !important"
  },
  gridContent: {
    maxWidth: "calc(100% - 48px)"
  },
  w48px: {
    width: 48
  },
  textPrimary: {
    color: theme.palette.primary.main
  },
  panelError: {
    fontSize: "0.6875rem",
    color: "#D93024"
  }
}));

const hasBreakField = (vehicle, field) =>
  vehicle && vehicle.breaks && vehicle.breaks[0] && vehicle.breaks[0][field];

export default function VehicleExpandablePanels({
  inputValuesRef,
  errors,
  setErrors,
  setSaveDisabled,
  vehicle,
  quantity,
  vehicleTypes,
  expanded,
  setExpanded,
  panelErrors,
  configuration,
  currentVehicleSelection,
  isNotAllowedToEdit
}) {
  const classes = useStyles();

  const vehiclesFromVRP = useSelector(state => state.project.vrp.vehicles);
  const editId = useSelector(state => state.extendable.id);

  const disabledVehicle =
    editId !== "multiple" &&
    vehiclesFromVRP.find(
      currentVehicle => currentVehicle?.type_id?.id === editId
    );

  // const inputValuesRef = useRef();

  quantity = quantity || 1;

  const [inputValues, setInputs] = useState({
    quantity: quantity || "1",
    capacity:
      vehicle && vehicle.typeId
        ? vehicleTypes.find(type => type.typeId === vehicle.typeId).capacity[0]
        : "100",
    name: vehicle ? vehicle.group_name : "",
    startLocation: vehicle
      ? vehicle.start_address
      : disabledVehicle?.start_address || null,
    endLocation: vehicle
      ? vehicle.endLocation
      : disabledVehicle?.end_address || null,
    startTime:
      vehicle && vehicle.earliest_start
        ? secondsConvert(vehicle.earliest_start)
        : new Date().setHours(8, 0),
    endTime:
      vehicle && vehicle.latest_end ? secondsConvert(vehicle.latest_end) : "",
    startBreak:
      vehicle && vehicle.break && vehicle.break.earliest
        ? secondsConvert(vehicle.break.earliest)
        : "",
    endBreak:
      vehicle && vehicle.break && vehicle.break.latest
        ? secondsConvert(vehicle.break.latest)
        : "",
    breakDuration:
      vehicle && vehicle.break && vehicle.break.duration
        ? vehicle.break.duration / 60
        : ""
  });

  const setInputValues = values => {
    inputValuesRef.current = values;
    setInputs(values);
  };

  // Handle input fields' changes
  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    setSaveDisabled(false);
  };

  // Handle max number check on keyUp
  const handleKeyup = e => {
    const { name, value, max } = e.target;
    setInputValues({
      ...inputValues,
      [name]: max && value > Number(max) ? max : value
    });
  };

  // Handle time input change
  const handleTimeChange = (val, name) => {
    setInputValues({
      ...inputValues,
      [name]: val
    });
    setSaveDisabled(false);
  };

  // Handle time input blur
  const isValid = (name, defaultVal) => {
    if (!inputValues[name]) {
      setInputValues({ ...inputValues, [name]: defaultVal });
      setErrors({ ...errors, startTime: "", [name]: "" });
      return false;
    }
    if (inputValues[name].toString() === "Invalid Date") {
      setErrors({ ...errors, [name]: "Invalid time format" });
      return false;
    }
    setErrors(state => ({
      ...state,
      [name]: ""
    }));
    return true;
  };

  const timeBetween = (start, end) => {
    if (
      inputValues[start]?.toString() !== "Invalid Date" &&
      inputValues[end]?.toString() !== "Invalid Date"
    )
      return setErrors(state => ({
        ...state,
        [start]:
          state[start] === "Invalid time format"
            ? state[start]
            : getHoursMins(inputValues[end]) <= getHoursMins(inputValues[start])
            ? "Invalid time window"
            : ""
      }));
  };

  const breakDur = name => {
    return setErrors({
      ...errors,
      breakDuration:
        Number(inputValues[name]) < 1 ? "Must be a positive number" : ""
    });
  };

  const handleAddressSelect = (field, value) => {
    if (value && value.place_id) {
      // Get place details
      const service = new window.google.maps.places.PlacesService(
        window.document.createElement("div")
      );
      service.getDetails(
        {
          placeId: value.place_id,
          language: "en",
          fields: ["address_components", "geometry", "formatted_address"]
        },
        function (PlaceResult, PlacesServiceStatus) {
          console.log(value, PlaceResult);

          setInputValues({ ...inputValues, [field]: PlaceResult });
        }
      );
    } else {
      setInputValues({ ...inputValues, [field]: value });
    }

    setSaveDisabled(false);
  };

  // Handle expand/collapse
  const handlePanelChange = useCallback(
    index => {
      setExpanded(
        expanded.includes(index) ? expanded.filter(i => i !== index) : [index]
      );
    },
    [expanded, setExpanded]
  );

  const openPanelOnTab = (e, index, reverse) => {
    if (
      e.keyCode === 9 &&
      ((reverse && e.shiftKey) || (!e.shiftKey && !reverse))
    ) {
      // if changing between first two, and both are open, do not auto close them
      if (expanded.length < 2 || index === 2) {
        setExpanded([index]);
      }
    }
  };

  const locationSummary = () => {
    const { name, startLocation } = inputValues;
    let str = "";

    name && (str += `, ${name}`);
    startLocation && (str += `, ${startLocation.formatted_address}`);

    return str.slice(2);
  };

  const workingHoursSummary = () => {
    const { startTime, endTime } = inputValues;
    let str = "";

    startTime &&
      (str += `${getHoursMins(startTime)}-${
        endTime ? getHoursMins(endTime) : ""
      }`);

    return str;
  };

  const lunchHoursSummary = () => {
    const { startBreak, endBreak } = inputValues;
    let str = "";

    startBreak &&
      endBreak &&
      (str += `${getHoursMins(startBreak)}-${getHoursMins(endBreak)}`);

    return str;
  };

  const getTypeCapacity = typeId => {
    const type = vehicleTypes.find(el => el?.type_id === typeId);

    return type && type.capacity ? String(type.capacity[0]) : "";
  };

  useEffect(() => {
    if (editId === "multipe") {
      setInputValues({
        ...inputValues,
        startLocation: null,
        endLocation: null
      });
      return;
    }

    const vehicleLocal = vehicle || {};
    setInputValues({
      quantity: quantity || "1",
      name: vehicleLocal.group_name || "",
      capacity: getTypeCapacity(vehicleLocal?.type_id) || "",
      startLocation:
        vehicleLocal.start_address || disabledVehicle?.start_address || null,
      endLocation:
        vehicleLocal.end_address || disabledVehicle?.end_address || null,
      startTime: vehicleLocal.earliest_start
        ? secondsConvert(vehicleLocal.earliest_start)
        : new Date().setHours(8, 0),
      endTime: vehicleLocal.latest_end
        ? secondsConvert(vehicleLocal.latest_end)
        : undefined,
      startBreak: hasBreakField(vehicle, "earliest")
        ? secondsConvert(vehicle.breaks[0].earliest)
        : "",
      endBreak: hasBreakField(vehicle, "latest")
        ? secondsConvert(vehicle.breaks[0].latest)
        : "",
      breakDuration: hasBreakField(vehicle, "duration")
        ? vehicle.breaks[0].duration / 60
        : ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, editId]);

  // useEffect(() => {
  //   if (currentVehicleSelection.length) {
  //     const filteredVehicleTypes = vehicleTypes.filter(vehicleType =>
  //       currentVehicleSelection.includes(vehicleType.id)
  //     );

  //     let startAddress, endAddress;
  //     const filteredStartAddresses = new Set(
  //       filteredVehicleTypes.map(
  //         vehicleType => vehicleType.start_address?.formatted_address
  //       )
  //     );
  //     const filteredEndAddresses = new Set(
  //       filteredVehicleTypes.map(
  //         vehicleType => vehicleType.end_address?.formatted_address
  //       )
  //     );

  //     if (filteredStartAddresses.size === 1) {
  //       startAddress = filteredVehicleTypes[0]?.start_address;
  //     }

  //     if (filteredEndAddresses.size === 1) {
  //       endAddress = filteredVehicleTypes[0]?.end_address;
  //     }

  //     // if (editId === "multipe") {
  //     //   setInputValues({
  //     //     ...inputValues,
  //     //     startLocation: startAddress || null,
  //     //     endLocation: endAddress || null
  //     //   });
  //     // }
  //     // if (
  //     //   startAddress ||
  //     //   (configuration.allow_reload && configuration.depot_address)
  //     // ) {
  //     //   setSaveDisabled(false);
  //     // } else {
  //     //   setSaveDisabled(true);
  //     // }
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentVehicleSelection]);

  return (
    <>
      {/* Expanding panels */}
      <Box
        overflow="auto"
        py={4}
        pl={4}
        pr={5}
        height="100%"
        key={vehicle && vehicle.vehicle_id}
      >
        {/* Start location and availability */}
        <Panel
          panelChange={e => handlePanelChange(0, e)}
          expanded={expanded.includes(0)}
          title="License plate, location and capacity"
          data-cy="editVehicle-locationPanel"
          summary={locationSummary()}
          published={false}
          icon={
            <MapIcon
              className={clsx({
                [classes.textPrimary]: expanded.includes(0)
              })}
            />
          }
          error={panelErrors[0]}
        >
          <Grid container spacing={1}>
            <Box component={Grid} className={classes.w48px} item xs="auto" />

            <Grid
              item
              xs
              className={clsx(
                classes.gridContent,
                classes.gridContentLocations
              )}
            >
              {/* Start location */}
              <Box position="relative">
                <VehicleAutocomplete
                  inputId="input-start-location"
                  dataCy="vehicleAutocomplete-start"
                  label="Start location*"
                  currentVehicleSelection={currentVehicleSelection}
                  initialValue={
                    configuration.allow_reload && configuration.depot_address
                      ? configuration.depot_address.formatted_address
                      : inputValues.startLocation
                      ? inputValues.startLocation.formatted_address
                      : ""
                  }
                  handleSelect={selectedItem =>
                    handleAddressSelect("startLocation", selectedItem)
                  }
                  disabled={
                    (!!configuration.allow_reload &&
                      !!configuration.depot_address) ||
                    isNotAllowedToEdit
                  }
                  helperText={errors.startLocation || "*Required"}
                  error={!!errors.startLocation}
                />
                {configuration.allow_reload && configuration.depot_address && (
                  <LightTooltip
                    text={
                      <>
                        Editing Start location is not possible, because you
                        switched on <strong>Allow reloads</strong> option.
                      </>
                    }
                    blackMode
                    fromRight
                  />
                )}
              </Box>

              {/* End location */}
              <Box position="relative">
                <VehicleAutocomplete
                  endLocation
                  inputId="input-end-location"
                  dataCy="vehicleAutocomplete-end"
                  label="End location"
                  currentVehicleSelection={currentVehicleSelection}
                  initialValue={
                    configuration.allow_reload && configuration.depot_address
                      ? configuration.depot_address.formatted_address
                      : inputValues.endLocation
                      ? inputValues.endLocation.formatted_address
                      : ""
                  }
                  disabled={
                    (!!configuration.allow_reload &&
                      !!configuration.depot_address) ||
                    isNotAllowedToEdit
                  }
                  openPanelOnTab={openPanelOnTab}
                  handleSelect={selectedItem =>
                    handleAddressSelect("endLocation", selectedItem)
                  }
                />
                {configuration.allow_reload && configuration.depot_address && (
                  <LightTooltip
                    text={
                      <>
                        Editing End location is not possible, because you
                        switched on <strong>Allow reloads</strong> option.
                      </>
                    }
                    blackMode
                    fromRight
                  />
                )}
                {/* {isNotAllowedToEdit && (
                  <LightTooltip
                    text={<>In view or completed mode changes not allowed</>}
                    blackMode
                    fromRight
                  />
                )} */}
              </Box>
            </Grid>
          </Grid>
        </Panel>

        {/* Drivers’ working hours  */}
        <Panel
          panelChange={() => handlePanelChange(1)}
          expanded={expanded.includes(1)}
          title="Drivers’ working hours"
          data-cy="editVehicle-hoursPanel"
          summary={workingHoursSummary()}
          published={false}
          icon={
            <TimeIcon
              className={clsx({
                [classes.textPrimary]:
                  expanded.length < 2 && expanded.includes(1)
              })}
            />
          }
          error={panelErrors[1]}
        >
          <Box mt={2} mb={0.5} width={"100%"}>
            <Grid container spacing={1}>
              <Box component={Grid} className={classes.w48px} item xs="auto" />

              {/* Name */}
              <Grid item xs className={classes.gridContent}>
                {/* Delivery hours */}
                <Form
                  data={[
                    [
                      {
                        startLabel: "From*",
                        endLabel: "To (optional)",
                        startName: "startTime",
                        endName: "endTime",
                        startDataCy: "startTime",
                        endDataCy: "endTime",
                        requiredStart: "*Required",
                        onKeyDownStart: e => openPanelOnTab(e, 0, true),
                        onKeyDownEnd: e => openPanelOnTab(e, 2),
                        onBlurStart: () => {
                          isValid("startTime", new Date().setHours(8, 0)) &&
                            timeBetween("startTime", "endTime");
                        },
                        onBlurEnd: () => {
                          isValid("endTime", null) &&
                            timeBetween("startTime", "endTime");
                        },
                        onChangeStart: val => {
                          handleTimeChange(val, "startTime");
                        },
                        onChangeEnd: val => {
                          handleTimeChange(val, "endTime");
                        },
                        componentType: "timeWindow",
                        disabled: isNotAllowedToEdit
                      }
                    ]
                  ]}
                  inputValues={inputValues}
                  errorValues={errors}
                />
                {/* {isNotAllowedToEdit && (
                  <LightTooltip
                    text={<>In view or completed mode changes not allowed</>}
                    blackMode
                    fromRight
                  />
                )} */}
              </Grid>
            </Grid>
          </Box>
        </Panel>

        {/* Lunch break  */}
        <Panel
          panelChange={() => handlePanelChange(2)}
          expanded={expanded.includes(2)}
          title="Lunch and coffee break"
          data-cy="editVehicle-breakPanel"
          summary={lunchHoursSummary()}
          published={false}
          icon={
            <CoffeeIcon
              className={clsx({
                [classes.textPrimary]: expanded.includes(2)
              })}
            />
          }
          error={panelErrors[2]}
        >
          <Grid container spacing={1}>
            <Box component={Grid} className={classes.w48px} item xs="auto" />

            <Grid item xs className={classes.gridContent}>
              <Box mt={2} mb={1} width="100%">
                <Form
                  data={[
                    [
                      {
                        renderInputField: true,
                        label: "Break duration",
                        name: "breakDuration",
                        type: "number",
                        dataCy: "breakDuration",
                        min: 1,
                        max: 1440,
                        onBlur: () => {
                          isValid("breakDuration", null) &&
                            breakDur("breakDuration");
                        },
                        onKeyUp: e => handleKeyup(e),
                        onKeyDown: e => {
                          openPanelOnTab(e, 1, true);
                          handleKeypress(e);
                        },
                        endAdornment: (
                          <InputAdornment position="end">Min.</InputAdornment>
                        ),
                        disabled: isNotAllowedToEdit
                      }
                    ],
                    [
                      {
                        startLabel: "From",
                        endLabel: "To",
                        startName: "startBreak",
                        endName: "endBreak",
                        startDataCy: "breakStartTime",
                        endDataCy: "breakEndTime",
                        onBlurStart: () => {
                          isValid("startBreak", null) &&
                            timeBetween("startBreak", "endBreak");
                        },
                        onBlurEnd: () => {
                          isValid("endBreak", null) &&
                            timeBetween("startBreak", "endBreak");
                        },
                        onChangeStart: val =>
                          handleTimeChange(val, "startBreak"),
                        onChangeEnd: val => handleTimeChange(val, "endBreak"),
                        componentType: "timeWindow",
                        disabled: isNotAllowedToEdit
                      }
                    ]
                  ]}
                  inputValues={inputValues}
                  errorValues={errors}
                  handleChange={handleChange}
                />
                {/* {isNotAllowedToEdit && (
                  <LightTooltip
                    text={<>In view or completed mode changes not allowed</>}
                    blackMode
                    fromRight
                  />
                )} */}
              </Box>
            </Grid>
          </Grid>
        </Panel>
      </Box>
    </>
  );
}
